import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FolderOpen, LogOut, Bell } from 'lucide-react';
import { useChAuth } from '../contexts/ChAuthContext';
import { chProjectService, chProfileService } from '../services';
import { isChStaffRole, ROLE_LABELS } from '../types';
import type { MentionCandidate } from '../lib/mentions';
import { chPostService, chCommentService } from '../services/postService';
import { chBrandingService } from '../services';
import { chNotificationService } from '../services/notificationService';
import { chPrintService } from '../services/printService';
import type { ChPostPrint } from '../types';
import { chSupabase } from '../lib/supabase';
import { ClientReviewContent } from '../components/ClientReviewContent';
import { chClientDecisionService, type ClientReviewData } from '../services/clientService';
import { useModal } from '../../contexts/ModalContext';

export function ClientReviewPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user, signOut, accessInfo } = useChAuth();
  const { toast } = useModal();
  const isClient = !accessInfo.isAgencyStaff;
  const navigate = useNavigate();
  const [data, setData] = useState<ClientReviewData | null>(null);
  const [team, setTeam] = useState<MentionCandidate[]>([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user || accessInfo.isAgencyStaff) return;
    chNotificationService.getUnreadCount(user.id).then(setUnread).catch(() => setUnread(0));
    return chNotificationService.subscribeToNew(user.id, () => setUnread(n => n + 1));
  }, [user, accessInfo.isAgencyStaff]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug || !user) return;
    loadProjectData();
  }, [slug, user]);

  async function loadProjectData() {
    try {
      setLoading(true);
      const project = await chProjectService.getBySlug(slug!);
      if (!project) {
        navigate('/contenthub');
        return;
      }

      const [posts, branding, profiles] = await Promise.all([
        chPostService.getByProject(project.id),
        chBrandingService.get(project.id),
        chProfileService.getAll().catch(() => []),
      ]);
      // Clients can tag the agency team in their comments
      setTeam(profiles
        .filter(p => isChStaffRole(p.role) && p.id !== user?.id)
        .map(p => ({ id: p.id, name: p.name, hint: ROLE_LABELS[p.role] })));

      const visible = posts.filter(p => ['em_revisao', 'aprovado', 'publicado'].includes(p.status));
      const printsByPost: Record<string, ChPostPrint[]> = await chPrintService
        .listByPosts(visible.map(p => p.id))
        .catch(() => ({}));

      const clientPosts = visible
        .map(p => ({
          id: p.id,
          title: p.title,
          caption: p.caption,
          platform: p.platform,
          platforms: p.platforms ?? null,
          content_type: p.content_type,
          scheduled_date: p.scheduled_date,
          published_at: p.published_at ?? null,
          media_url: p.media_url,
          media_urls: p.media_urls ?? null,
          thumbnail_url: p.thumbnail_url,
          status: p.status,
          client_decision: p.client_decision,
          current_version: p.current_version,
          external_link: p.external_link,
          metrics: p.metrics ?? null,
          metrics_by_platform: p.metrics_by_platform ?? null,
          metrics_updated_at: p.metrics_updated_at ?? null,
          prints: printsByPost[p.id] || [],
        }));

      setData({
        valid: true,
        project: {
          id: project.id,
          name: project.name,
          slug: project.slug,
          followers: project.followers ?? null,
          followers_updated_at: project.followers_updated_at ?? null,
        },
        branding: branding ? {
          logo_url: branding.logo_url,
          primary_color: branding.primary_color,
          background_color: branding.background_color,
          background_image_url: branding.background_image_url,
          accent_color: branding.accent_color,
        } : null,
        posts: clientPosts,
      });
    } catch {
      navigate('/contenthub');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !user) return null;

  const decide = async (postId: string, decision: 'approved' | 'changes_requested', note?: string) => {
    try {
      const result = await chClientDecisionService.decide(postId, decision, note);
      if (result.unavailable) return null;
      if (!result.success) toast.error(result.error || 'Não foi possível registar a decisão');
      return { success: result.success };
    } catch {
      toast.error('Não foi possível registar a decisão. Tenta de novo.');
      return { success: false };
    }
  };

  const actions = {
    async approve(postId: string, actorName: string) {
      const viaServer = await decide(postId, 'approved');
      if (viaServer) return viaServer;
      await chSupabase.from('ch_posts').update({ client_decision: 'approved', updated_at: new Date().toISOString() }).eq('id', postId);
      await chSupabase.from('ch_approvals').insert({ post_id: postId, user_id: user.id, user_name: actorName, action: 'client_approved', note: null });
      const post = await chPostService.getById(postId);
      if (post) {
        await chNotificationService.notifyProjectMembers(
          post.project_id,
          user.id,
          postId,
          'client_approved',
          `Cliente ${actorName} aprovou "${post.title}"`
        );
      }
      return { success: true };
    },
    async requestChanges(postId: string, actorName: string, comment: string) {
      const viaServer = await decide(postId, 'changes_requested', comment);
      if (viaServer) return viaServer;
      await chSupabase.from('ch_posts').update({ client_decision: 'changes_requested', updated_at: new Date().toISOString() }).eq('id', postId);
      await chSupabase.from('ch_comments').insert({ post_id: postId, author_id: user.id, author_name: actorName, type: 'client', text: comment });
      await chSupabase.from('ch_approvals').insert({ post_id: postId, user_id: user.id, user_name: actorName, action: 'changes_requested', note: comment });
      const post = await chPostService.getById(postId);
      if (post) {
        await chNotificationService.notifyProjectMembers(
          post.project_id,
          user.id,
          postId,
          'changes_requested',
          `Cliente ${actorName} pediu alterações em "${post.title}"`
        );
      }
      return { success: true };
    },
    async addComment(postId: string, actorName: string, comment: string, mentions?: string[]) {
      let created;
      try {
        created = await chCommentService.create({ post_id: postId, author_id: user.id, author_name: actorName, type: 'client', text: comment, mentions });
      } catch {
        toast.error('Não foi possível enviar o comentário');
        return { success: false };
      }
      if (mentions && mentions.length > 0) {
        await chNotificationService.notifyMentions(created.id).catch(() => { /* comment is saved */ });
      }
      const post = await chPostService.getById(postId);
      if (post) {
        await chNotificationService.notifyProjectMembers(
          post.project_id,
          user.id,
          postId,
          'commented',
          `Cliente ${actorName} comentou em "${post.title}"`
        );
      }
      return { success: true };
    },
    async getComments(postId: string) {
      const comments = await chCommentService.getByPost(postId);
      const clientOnly = comments.filter(c => c.type === 'client');
      return { success: true, comments: clientOnly };
    },
  };

  return (
    <>
      {isClient && (
        <div className="fixed top-3 right-3 z-40 flex gap-2">
          <button
            onClick={() => navigate('/contenthub/notificacoes')}
            className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
            aria-label={unread > 0 ? `Notificações (${unread} por ler)` : 'Notificações'}
          >
            <Bell className="w-3.5 h-3.5" />
            {unread > 0 && (
              <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#FF4FA3] text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/contenthub?escolher=1')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            Projetos
          </button>
          <button
            onClick={async () => { await signOut(); navigate('/login'); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-gray-700 shadow-sm hover:bg-white"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sair
          </button>
        </div>
      )}
      <ClientReviewContent
        data={data}
        token=""
        actorName={user.name}
        onNeedName={() => {}}
        actions={actions}
        mentionCandidates={team}
      />
    </>
  );
}
